<template>
    <div
        ref="inputWrapper"
        :class="[
            'dt-input-wrapper',
            { 'is-ai': isAI === true },
            { 'has-error': isError === true },
        ]"
    >
        <input
            :id="id"
            type="text"
            :class="['dt-input', { error: isError }]"
            :placeholder="placeholder"
            :required="internalRequired"
            :data-payload="fullPayload"
            v-model="value"
            @input="handleInput"
            @focus="handleFocus"
            @blur="validateDate"
        />
        <div class="input-ai-style"></div>
        <img
            v-if="isAI"
            src="@/assets/customizer_icon_magic_wand.svg"
            class="magic-wand-icon"
            width="22"
            height="22"
            alt="Zauberstab Icon"
        />
        <div v-if="isError" class="error-message">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
import { nextTick } from "vue";
import EventBus from "../event-bus";
import { trackEvent } from "../utils/tracking";
import { safeGetJson, safeSetJson } from "../utils/safeStorage";
import { builderText } from "../utils/customizerTexts";

export default {
    name: "DTDateInput",
    props: {
        id: {
            type: String,
            default: "",
        },
        payload: {
            type: String,
            required: true,
        },
        sharePayload: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "",
        },
        required: {
            type: Boolean,
            default: false,
        },
        dateFormat: {
            type: String,
            default: "dd.mm.yyyy",
            validator: function (value) {
                return ["dd.mm.yyyy", "mm.dd.yyyy", "dd/mm/yyyy", "mm/dd/yyyy"].includes(
                    value
                );
            },
        },
        errorMessageFormat: {
            type: String,
            default: "",
        },
        errorMessageRequired: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isAI: false,
            isError: false,
            errorMessage: "",
            fullPayload: this.payload,
            internalRequired: this.required,
            value: "",
            currentBookId: "unknown_book",
            currentStep: "unknown_step",
        };
    },
    inject: ["pageSettings"],
    computed: {
        formattedDateFormat() {
            return this.dateFormat.toUpperCase();
        },
        dateInputSeparator() {
            return this.dateFormat.includes("/") ? "/" : ".";
        },
        dateFormatRegex() {
            if (this.dateFormat === "dd.mm.yyyy") {
                return /^([0-9]{1,2})[-./]([0-9]{1,2})[-./]([0-9]{2,4})$/;
            } else {
                return /^([0-9]{1,2})[-./]([0-9]{1,2})[-./]([0-9]{2,4})$/;
            }
        },
    },
    mounted() {
        if (this.$route) {
            this.currentBookId = this.$route.params?.bookId || "unknown_book";
            this.currentStep = this.$route.name || "unknown_step";
        }

        requestAnimationFrame(() => {
            this.$nextTick(() => {
                EventBus.on(
                    "throwErrorForRequiredInputs",
                    this.handleErrorState
                );
                this.fullPayload = this.getFullPayload();
                this.loadFromLocalStorage();
                
                this.$nextTick(() => {
                    if (this.value && this.value.trim() !== "") {
                        if (!this.dateFormatRegex.test(this.value)) {
                            this.isError = true;
                            this.errorMessage = builderText(this.errorMessageFormat, "dateFormatError").replace("%format%", this.formattedDateFormat);
                        }
                    }
                });
            });
        });
    },
    beforeUnmount() {
        EventBus.off("throwErrorForRequiredInputs", this.handleErrorState);
    },
    methods: {
        handleFocus() {
            this.handleScrollToOnFocus();
            if (process.env.VUE_APP_DEV_MODE !== "true") {
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', () => this.sendFocusEvent(), { once: true });
                } else {
                    this.sendFocusEvent();
                }
            }
        },
        
        sendFocusEvent() {
            trackEvent(this, {
                event: "input_interaction_start",
                category: "Customizer",
                action: "FocusOnInput",
                label: this.placeholder, 
                input_id: this.id,
                input_type: "date",
                input_payload: this.fullPayload,
                value_length: this.value.length,
            });
        },
        
        handleScrollToOnFocus() {
        },

        getFullPayload() {
            if (!this.payload || this.payload.trim() === "") {
                return null;
            }

            let element = this.$el;

            while (element && element.parentElement) {
                if (element.parentElement.classList.contains("tab-content")) {
                    const prefix = element.parentElement.getAttribute(
                        "data-payload-prefix"
                    );
                    if (prefix) {
                        return `${prefix}.${this.payload}`;
                    }
                }

                if (element.parentElement.classList.contains("milestone")) {
                    return `milestones.${this.payload}`;
                }

                element = element.parentElement;
            }

            return this.payload;
        },
        loadFromLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let current = stepValues;
            let sharedCurrent = sharedValues;

            for (let i = 0; i < keys.length; i++) {
                if (!current[keys[i]]) {
                    current[keys[i]] = i === keys.length - 1 ? {} : {};
                }
                current = current[keys[i]];

                if (!sharedCurrent[keys[i]]) {
                    sharedCurrent[keys[i]] = i === keys.length - 1 ? {} : {};
                }
                sharedCurrent = sharedCurrent[keys[i]];
            }

            if (this.sharePayload && sharedCurrent.value) {
                this.value = sharedCurrent.value;
                current.value = sharedCurrent.value;
            } else if (current.value) {
                this.value = current.value;
            } else {
                current.value = this.value === "" ? "" : this.value;
                this.value = current.value;
            }

            current.required = this.required;
            if (this.sharePayload) {
                sharedCurrent.value = this.value;
                sharedCurrent.required = this.required;
            }

            nextTick(() => {
                if (
                    this.$refs.inputWrapper &&
                    this.$refs.inputWrapper.style.display === "none"
                ) {
                    current.required = false;
                }
            });

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        saveToLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let currentStep = stepValues;
            let currentShared = sharedValues;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!currentStep[keys[i]]) {
                    currentStep[keys[i]] = {};
                }
                currentStep = currentStep[keys[i]];

                if (this.sharePayload) {
                    if (!currentShared[keys[i]]) {
                        currentShared[keys[i]] = {};
                    }
                    currentShared = currentShared[keys[i]];
                }
            }

            currentStep[keys[keys.length - 1]] = {
                ...currentStep[keys[keys.length - 1]],
                value: this.value,
                required: this.internalRequired,
            };

            if (this.sharePayload) {
                currentShared[keys[keys.length - 1]] = {
                    ...currentShared[keys[keys.length - 1]],
                    value: this.value,
                    required: this.internalRequired,
                };
            }

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        handleInput(event) {
            this.isAI = false;
            
            const validInput = event.target.value.replace(/[^0-9\-./]/g, '');
            this.value = validInput;
            
            this.formatDateInput();
            
            this.validateDate();
            
            this.saveToLocalStorage();
        },
        formatDateInput() {
            let digits = this.value.replace(/\D/g, '');
            
            if (digits.length > 0) {
                const separator = this.dateInputSeparator;

                if (digits.length <= 2) {
                    this.value = digits;
                } else if (digits.length <= 4) {
                    this.value = `${digits.substring(0, 2)}${separator}${digits.substring(2)}`;
                } else if (digits.length <= 6) {
                    this.value = `${digits.substring(0, 2)}${separator}${digits.substring(2, 4)}${separator}${digits.substring(4, 6)}`;
                } else {
                    this.value = `${digits.substring(0, 2)}${separator}${digits.substring(2, 4)}${separator}${digits.substring(4, 8)}`;
                }
            }
        },
        validateDate() {
            let isValid = true;
            
            if (!this.value || this.value.trim() === "") {
                const isSubmitOrBlur = document.activeElement !== this.$refs.inputWrapper.querySelector('input');
                this.isError = isSubmitOrBlur && this.internalRequired;
                this.errorMessage = this.isError ? builderText(this.errorMessageRequired, "fieldRequired") : "";
                isValid = !this.internalRequired;
            } else {
                if (!this.dateFormatRegex.test(this.value)) {
                    this.isError = true;
                    this.errorMessage = builderText(this.errorMessageFormat, "dateFormatError").replace("%format%", this.formattedDateFormat);
                    isValid = false;
                } else {
                    this.isError = false;
                    this.errorMessage = "";
                }
            }
            
            this.$emit('validation', {
                id: this.id,
                payload: this.fullPayload,
                isValid: isValid
            });
            
            EventBus.emit('dateInputValidation', {
                id: this.id,
                payload: this.fullPayload,
                isValid: isValid
            });
            
            return isValid;
        },
        handleErrorState(newState) {
            if (newState === false) {
                this.isError = false;
                this.errorMessage = "";
                
                this.$emit('validation', {
                    id: this.id,
                    payload: this.fullPayload,
                    isValid: true
                });
                
                EventBus.emit('dateInputValidation', {
                    id: this.id,
                    payload: this.fullPayload,
                    isValid: true
                });
                
                return true;
            }

            if (this.internalRequired && this.value.trim() === "") {
                this.isError = true;
                this.errorMessage = builderText(this.errorMessageRequired, "fieldRequired");
                
                this.$emit('validation', {
                    id: this.id,
                    payload: this.fullPayload,
                    isValid: false
                });
                
                EventBus.emit('dateInputValidation', {
                    id: this.id,
                    payload: this.fullPayload,
                    isValid: false
                });
                
                return false;
            }

            return this.validateDate();
        },
        setAIValue(newValue) {
            this.isAI = true;

            let i = 0;
            this.value = "";
            const interval = setInterval(() => {
                if (i < newValue.length) {
                    this.value += newValue.charAt(i);
                    i++;
                } else {
                    clearInterval(interval);
                    this.validateDate();
                    this.saveToLocalStorage();
                }
            }, 25);
        },
        setRequired(newState) {
            this.internalRequired = newState;
            this.saveToLocalStorage();
        },
        resetValue() {
            this.value = "";
            this.isError = false;
            this.errorMessage = "";
            this.isAI = false;
            this.saveToLocalStorage();
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.dt-input-wrapper {
    position: relative;
    width: 100%;

    .dt-input {
        position: relative;
        z-index: 3;
        width: 100%;
        padding: 16px 32px;
        border: 2px solid $color-light-gray;
        border-radius: 46px;
        background-color: $color-white;
        transition: border-color 0.3s;
        @include font-body-m;

        & {
            color: $color-input-active-text;
        }

        @media (max-width: $breakpoint-tablet) {
            padding: 16px 24px;
            border-radius: 36px;
            font-size: 16px;
        }

        &:active,
        &:focus {
            border-width: 2px;
            border-color: #625f6b;
            outline: none;
            box-shadow: none;
        }

        &::placeholder {
            color: $color-placeholder-text;
        }

        &.error {
            border-color: $color-input-error;
        }
    }

    .input-ai-style {
        position: absolute;
        z-index: 1;
        display: none;
        top: -2px;
        right: -3px;
        bottom: 1px;
        left: -3px;
        border-radius: 46px;
        background: $ai-gradient;

        @media (max-width: $breakpoint-tablet) {
            border-radius: 36px;
        }

        &::before {
            content: "";
            position: absolute;
            z-index: 2;
            top: 2px;
            right: 2px;
            bottom: 2px;
            left: 2px;
            border-radius: 46px;
            background: $color-white;

            @media (max-width: $breakpoint-tablet) {
                border-radius: 36px;
            }
        }
    }

    .magic-wand-icon {
        position: absolute;
        z-index: 4;
        top: 17px;
        right: 24px;
    }

    .error-message {
        position: relative;
        z-index: 2;
        margin-top: 8px;
        padding-left: 32px;
        color: $color-input-error;
        font-size: 14px;

        @media (max-width: $breakpoint-tablet) {
            padding-left: 24px;
        }
    }

    &.is-ai {
        .dt-input {
            padding-right: 58px;
            border-color: transparent;
            background: $ai-gradient;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            box-shadow: none;
        }

        .input-ai-style {
            display: block;
        }
    }
}
</style>
